import React, { useState, useEffect } from "react";
import Api from "../../../../core/data/redux/utils/apiCall";
import { BASE_URLS, API_URLS } from "../../../../core/data/redux/apis/urls";
import { formatNatureOfDuty } from "./common/nocUtils";
import { handleDocumentClick } from "./common/documentUtils";
import { NocHeader } from "./common/NocHeader";
import { NocEmployeeCard } from "./common/NocEmployeeCard";
import { NocWorkHistoryCard } from "./common/NocWorkHistoryCard";
import { NocChatSection } from "./common/NocChatSection";
import { NocActionModal } from "./common/NocActionModal";

interface NocOnlineCourseApprovalProps {
  id: string;
  nocData: any;
  onRefresh: () => void;
  onBack: () => void;
  onShowToast: (title: string, message: string, type: "success" | "danger" | "warning" | "primary") => void;
}

export const NocOnlineCourseApproval: React.FC<NocOnlineCourseApprovalProps> = ({
  id,
  nocData,
  onRefresh,
  onBack,
  onShowToast,
}) => {
  const [hRemarks, setHRemarks] = useState<string>("");
  const [contactNumber, setContactNumber] = useState<string>("");
  const [contactNumberError, setContactNumberError] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<"A" | "R" | null>(null);

  const handleApproveReject = async (action: "A" | "R") => {
    // Validation for Contact Number if editable
    const isContactEditable =
      nocData?.enabled_fields_fields_json?.ContactNumber === "X" ||
      nocData?.enabled_fields_json?.ContactNumber === "X" ||
      nocData?.enabled_fields_fields_json?.Phone === "X" ||
      nocData?.enabled_fields_json?.Phone === "X" ||
      nocData?.enabled_fields_fields_json?.contactNumber === "X" ||
      nocData?.enabled_fields_json?.contactNumber === "X";

    if (isContactEditable) {
      if (!contactNumber || !/^\d{10}$/.test(contactNumber)) {
        setContactNumberError("Contact number must be exactly 10 digits");
        onShowToast("Error", "Contact number must be exactly 10 digits.", "danger");
        return;
      }
    }

    // Validation for Mandatory Remarks on Rejection
    if (action === "R" && !(hRemarks || "").trim()) {
      onShowToast("Error", "Approver remarks/comments are mandatory for rejection.", "danger");
      const remarksEl = document.getElementById("approver-remarks-textarea");
      if (remarksEl) {
        remarksEl.scrollIntoView({ behavior: "smooth", block: "center" });
        remarksEl.focus();
      }
      return;
    }

    setIsProcessing(true);
    setProcessingAction(action);

    try {
      const payload = {
        Approve_Reject_flag: action,
        ReferenceNumber: nocData.referenceNumber,
        remarks: "",
        hRemarks: (hRemarks || "").trim(),
        ContactNumber: contactNumber,
        contactNumber: contactNumber,
      };

      console.log("Online Course Approval Payload:", payload);
      const res: any = await Api.post(`${BASE_URLS.BASE_URL}noc-onlinecourses/approve_reject_action`, payload);

      if (res?.data) {
        const actionText = action === "A" ? "approved" : "rejected";
        onShowToast("Success", `Online Course NOC application ${actionText} successfully`, "success");
        setTimeout(() => {
          onBack();
        }, 1500);
      }
    } catch (err: any) {
      console.error("Online Course Approval Error:", err);
      onShowToast("Error", err?.message || "Action failed", "danger");
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  const [courseDeptInfo, setCourseDeptInfo] = useState<any>(null);

  const formatDate = (dateString: string | Date | undefined | null): string => {
    if (!dateString || dateString === "Invalid date") return "-";
    try {
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return "-";
    }
  };

  useEffect(() => {
    if (nocData) {
      setContactNumber(
        nocData?.contactNumber ||
        nocData?.employee?.phone ||
        nocData?.employee?.details?.phone ||
        ""
      );
    }
  }, [nocData]);

  useEffect(() => {
    let isMounted = true;
    const fetchCourseDetails = async () => {
      if (!nocData) return;

      const activeCourse = nocData?.online_course || nocData?.noc_online_course || nocData;
      const existingDept =
        activeCourse?.course_category?.course_departments ||
        activeCourse?.course_departments;

      if (
        existingDept &&
        (existingDept.plateform || existingDept.platform) &&
        existingDept.department &&
        existingDept.course
      ) {
        if (isMounted) setCourseDeptInfo(existingDept);
        return;
      }

      // 1. Try FIND_ONE_ONLINE_COURSE if we have id or referenceNumber
      const refNo = nocData?.referenceNumber || nocData?.id;
      if (refNo) {
        try {
          const singleRes = await Api.post(API_URLS.NOC.FIND_ONE_ONLINE_COURSE, {
            id: nocData?.id,
            referenceNumber: nocData?.referenceNumber,
          });
          const fullDetail = singleRes?.data?.data || singleRes?.data;
          const fullDept =
            fullDetail?.course_category?.course_departments ||
            fullDetail?.course_departments;
          if (fullDept && isMounted) {
            setCourseDeptInfo(fullDept);
            return;
          }
        } catch {
          // ignore
        }
      }

      // 2. Fetch master COURSE_DEPARTMENT_LIST by category
      const rawCat = activeCourse.onlineCourseCategory;
      const categoryId =
        (rawCat === "Specialized Courses" || rawCat === "A2" || rawCat === "2" ? "2" : rawCat === "General Courses" || rawCat === "A1" || rawCat === "1" ? "1" : rawCat) ||
        activeCourse.course_category?.category_id ||
        activeCourse.course_category?.id;

      try {
        const res = await Api.post(API_URLS.MASTERS.COURSE_DEPARTMENT_LIST, {
          category_id: categoryId || undefined,
        });
        const deptList = res?.data?.data || res?.data || [];
        if (Array.isArray(deptList) && deptList.length > 0 && isMounted) {
          const courseKey = activeCourse.keyForCourse;
          const courseNameStr = activeCourse.onlineCourseName || activeCourse.courseName;

          const matched = deptList.find(
            (item: any) =>
              (courseKey && String(item.course_id) === String(courseKey)) ||
              (courseNameStr && String(item.course).trim().toLowerCase() === String(courseNameStr).trim().toLowerCase()) ||
              (courseNameStr && String(item.course_id) === String(courseNameStr))
          );
          if (matched) {
            setCourseDeptInfo(matched);
          }
        }
      } catch (err) {
        console.warn("COURSE_DEPARTMENT_LIST fetch error in approval:", err);
      }
    };

    fetchCourseDetails();
    return () => {
      isMounted = false;
    };
  }, [nocData]);

  const courseDetails = nocData?.online_course || nocData?.noc_online_course || {};
  const activeDept =
    courseDeptInfo ||
    courseDetails?.course_category?.course_departments ||
    courseDetails?.course_departments ||
    nocData?.course_category?.course_departments ||
    nocData?.course_departments ||
    {};

  const categoryTitle = (() => {
    const rawCat =
      activeDept?.category?.category_title ||
      courseDetails?.category?.category_title ||
      courseDetails?.course_category?.category_title ||
      nocData?.course_category?.category_title ||
      courseDetails?.onlineCourseCategory ||
      nocData?.onlineCourseCategory ||
      nocData?.courseCategory ||
      "";
    if (rawCat === "1" || rawCat === "A1") return "General Courses";
    if (rawCat === "2" || rawCat === "A2") return "Specialized Courses";
    return rawCat || "-";
  })();

  const deptTitle =
    activeDept?.department ||
    courseDetails?.course_category?.course_departments?.department ||
    courseDetails?.onlineCourseDepartment ||
    nocData?.onlineCourseDepartment ||
    courseDetails?.department ||
    nocData?.department ||
    "-";

  const courseName =
    activeDept?.course ||
    courseDetails?.onlineCourseName ||
    nocData?.onlineCourseName ||
    courseDetails?.course_category?.course_departments?.course ||
    nocData?.course_category?.course_departments?.course ||
    courseDetails?.courseName ||
    nocData?.courseName ||
    "-";

  const platform =
    activeDept?.plateform ||
    activeDept?.platform ||
    courseDetails?.course_category?.course_departments?.plateform ||
    courseDetails?.course_category?.course_departments?.platform ||
    nocData?.course_category?.course_departments?.plateform ||
    nocData?.course_category?.course_departments?.platform ||
    courseDetails?.platform ||
    courseDetails?.plateform ||
    nocData?.platform ||
    nocData?.plateform ||
    "-";

  const rawDuration =
    activeDept?.duration ??
    courseDetails?.course_category?.course_departments?.duration ??
    courseDetails?.duration ??
    nocData?.durationOfOnlineCourse ??
    nocData?.duration ??
    "";
  const durationStr = String(rawDuration).trim();
  const durationNum = parseInt(durationStr, 10);
  const duration =
    durationStr !== "" && durationStr !== "null" && durationStr !== "undefined"
      ? durationStr.toLowerCase().includes("week")
        ? durationStr
        : !isNaN(durationNum)
          ? `${durationNum} Weeks`
          : durationStr
      : "-";

  const rawFee =
    activeDept?.course_fee ??
    courseDetails?.course_category?.course_departments?.course_fee ??
    courseDetails?.courseFee ??
    nocData?.courseFee ??
    courseDetails?.course_fee ??
    nocData?.course_fee ??
    nocData?.onlineCourseFee ??
    nocData?.fee;
  const fee =
    rawFee !== undefined && rawFee !== null && String(rawFee).trim() !== ""
      ? String(rawFee).trim()
      : "-";

  const contributor =
    activeDept?.contributor ||
    activeDept?.contributors ||
    courseDetails?.course_category?.course_departments?.contributor ||
    nocData?.course_category?.course_departments?.contributor ||
    courseDetails?.contributor ||
    nocData?.contributor ||
    courseDetails?.contributors ||
    nocData?.contributors ||
    "-";

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <NocHeader
          title="NOC for Online Course Approval"
          breadcrumbTitle="Online Course Request Details"
          nocType="NOC_ONLINECOURSE"
          nocData={nocData}
          onBack={onBack}
          onRefresh={onRefresh}
          onShowToast={onShowToast}
        />

        {/* Employee & Application Info */}
        <NocEmployeeCard
          nocData={nocData}
          nocType="NOC_ONLINECOURSE"
          editableContactNumber={contactNumber}
          onContactNumberChange={(val) => {
            setContactNumber(val);
            if (/^\d{10}$/.test(val)) setContactNumberError("");
          }}
          contactNumberError={contactNumberError}
        />

        {/* Course Details as per HR Compendium Table Card */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-book me-2 text-primary"></i>
              Course details as per HR Compendium
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-bordered table-striped mb-0 text-center align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Category</th>
                    <th>Department</th>
                    <th>Online Course Name</th>
                    <th>Platform</th>
                    <th>Duration of course</th>
                    <th>Online course fee</th>
                    <th>Contributors</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ backgroundColor: "#ffeba1" }} className="fw-medium">{categoryTitle}</td>
                    <td style={{ backgroundColor: "#ffeba1" }} className="fw-medium">{deptTitle}</td>
                    <td style={{ backgroundColor: "#ffeba1" }} className="fw-bold text-dark">{courseName}</td>
                    <td style={{ backgroundColor: "#ffeba1" }}>{platform}</td>
                    <td style={{ backgroundColor: "#ffeba1" }}>{duration}</td>
                    <td style={{ backgroundColor: "#ffeba1" }} className="fw-bold text-success">
                      {fee !== "-" ? (String(fee).startsWith("₹") ? fee : `₹ ${fee}`) : "-"}
                    </td>
                    <td style={{ backgroundColor: "#ffeba1" }}>{contributor}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Additional Course Details */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-notebook me-2 text-primary"></i>
              Additional Course Details
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">
                    Category of Online Course
                  </small>
                  <div className="fw-bold text-dark fs-15">{categoryTitle}</div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">
                    Course Department
                  </small>
                  <div className="fw-bold text-dark fs-15">{deptTitle}</div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">
                    Course Name
                  </small>
                  <div className="fw-bold text-primary fs-15">{courseName}</div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">
                    Nature of Duty Performing
                  </small>
                  <div className="fw-bold text-dark fs-15">
                    {formatNatureOfDuty(nocData?.natureOfDutyPerfo || nocData?.natureOfDuty, nocData?.nature_of_duty)}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">
                    Date of Course Joining
                  </small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(nocData?.dateOfCourseJoining)}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">
                    Last Date of Registration
                  </small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(nocData?.lastDateOfRegistration)}
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">
                    Remarks
                  </small>
                  <div className="fw-bold text-dark fs-15">
                    {nocData?.remarks || nocData?.Remarks || "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Previous Reimbursements for Online Courses */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-briefcase me-2 text-primary"></i>
              Previous Reimbursements for Online Courses
            </h5>
          </div>
          <div className="card-body">
            {Array.isArray(nocData?.previousReimbursements) && nocData.previousReimbursements.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover table-bordered mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Category</th>
                      <th>Platform</th>
                      <th>Course Duration</th>
                      <th>Course Name</th>
                      <th>Date of Joining Course</th>
                      <th>Completion Date</th>
                      <th>Online Course Fee</th>
                      <th>Reimbursement Date</th>
                      <th>End of Block</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nocData.previousReimbursements.map((item: any, idx: number) => (
                      <tr key={idx}>
                        <td>
                          {(typeof item.category === "object"
                            ? item.category?.category_title || item.category?.category_name
                            : item.category) || item.category_title || "-"}
                        </td>
                        <td>{item.platform || item.plateform || "-"}</td>
                        <td>{item.courseDuration || item.duration || "-"}</td>
                        <td>{item.courseName || item.onlineCourseName || "-"}</td>
                        <td>{formatDate(item.dateOfJoining || item.dateOfCourseJoining)}</td>
                        <td>{formatDate(item.completionDate || item.courseEndingDate)}</td>
                        <td>
                          {item.courseFee || item.onlineCourseFee
                            ? (String(item.courseFee || item.onlineCourseFee).startsWith("₹")
                              ? (item.courseFee || item.onlineCourseFee)
                              : `₹ ${item.courseFee || item.onlineCourseFee}`)
                            : "-"}
                        </td>
                        <td>{formatDate(item.reimbursementDate || item.dateOfReimbursement)}</td>
                        <td>
                          {formatDate(item.endOfBlock) !== "-"
                            ? formatDate(item.endOfBlock)
                            : item.endOfBlock || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div>
                <div
                  className="alert text-center fw-bold text-dark py-2 mb-3"
                  style={{ backgroundColor: "#ffc107", border: "none", borderRadius: "6px" }}
                >
                  No Previous Reimbursement Record Available
                </div>
                <div className="row g-3">
                  <div className="col-md-4 col-sm-6">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Category</small>
                      <div className="fw-bold text-dark fs-15">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Platform</small>
                      <div className="fw-bold text-dark fs-15">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Course Duration</small>
                      <div className="fw-bold text-dark fs-15">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Course Name</small>
                      <div className="fw-bold text-dark fs-15">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Date of Joining Course</small>
                      <div className="fw-bold text-dark fs-15">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Completion Date</small>
                      <div className="fw-bold text-dark fs-15">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Online Course Fee</small>
                      <div className="fw-bold text-dark fs-15">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Reimbursement Date</small>
                      <div className="fw-bold text-dark fs-15">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">End of Block</small>
                      <div className="fw-bold text-dark fs-15">-</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Others */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-dots-circle-horizontal me-2 text-primary"></i>
              Others
            </h5>
          </div>
          <div className="card-body">
            <div className="p-3 mb-3 rounded" style={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
              <h6 className="fw-bold mb-2 text-dark">
                <i className="ti ti-info-circle me-1 text-primary"></i>
                Instructions
              </h6>
              <ul
                className="mb-0 ps-4 text-muted small d-flex flex-column gap-1"
                style={{ listStyleType: "disc", lineHeight: "1.6" }}
              >
                <li>
                  For the Web URL field, Please provide link to the programme brochure / website URL, having details of fees, last date of registration, etc.
                </li>
                <li>
                  Updated course fee is to be filled if the course fees is diffrent from the approved amount in HR Compendium.
                </li>
                <li>
                  If the fee is in dollars or another currency, provide the amount in INR.
                </li>
                <li>
                  If there is no fixed last registration date, Then please enter today's date.
                </li>
              </ul>
            </div>

            <div className="row g-3">
              <div className="col-12">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Web URL</small>
                  <div className="fw-bold text-dark fs-15">
                    {nocData?.courseBrochure ? (
                      nocData.courseBrochure.startsWith("http://") || nocData.courseBrochure.startsWith("https://") ? (
                        <a
                          href={nocData.courseBrochure}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary text-decoration-underline text-break"
                        >
                          {nocData.courseBrochure}
                        </a>
                      ) : (
                        <span
                          className="text-primary cursor-pointer text-decoration-underline text-break"
                          onClick={() => handleDocumentClick({ fileName: nocData.courseBrochure }, "NOC_ONLINECOURSE", onShowToast)}
                        >
                          {nocData.courseBrochure}
                        </span>
                      )
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Updated Course Fee in INR</small>
                  <div className="fw-bold text-dark fs-15">
                    {nocData?.courseFee
                      ? (String(nocData.courseFee).startsWith("₹") ? nocData.courseFee : `₹ ${nocData.courseFee}`)
                      : "-"}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Last Date of Registration of Course</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(nocData?.lastDateOfRegistration)}
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Probable Date of Commencement of Course</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(nocData?.dateOfCourseJoining)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Declaration */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-shield-check me-2 text-primary"></i>
              Declaration
            </h5>
          </div>
          <div className="card-body">
            <div className="d-flex align-items-start gap-3 p-3 rounded bg-light border">
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle text-white mt-1"
                style={{ width: "28px", height: "28px", backgroundColor: "#28a745" }}
              >
                <i className="ti ti-check fs-16"></i>
              </div>
              <p className="mb-0 text-dark fw-medium" style={{ lineHeight: "1.6" }}>
                I hereby declare that the information given above is true and my duty hours in DMRC would not get disturbed in any case. I accept the terms &amp; conditions above. I may kindly be granted permission to enroll for Online Course.
              </p>
            </div>
          </div>
        </div>

        {/* Terms & Conditions */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-list-check me-2 text-primary"></i>
              Terms &amp; Conditions
            </h5>
          </div>
          <div className="card-body">
            <ul
              className="mb-0 ps-4 text-muted d-flex flex-column gap-2"
              style={{ listStyleType: "disc", lineHeight: "1.6" }}
            >
              <li>
                Claim will be reimbursed after qualifying the course and submission of
                certificate copy and payment details.
              </li>
              <li>
                Provision for accessories such as computer, peripherals, internet
                connection etc. will not be provided by DMRC and the employee will
                arrange the same.
              </li>
              <li>The online courses should not be pursued during office/duty hours.</li>
              <li>
                Any kind of special consideration, leave, transfer or shifting of duty
                to pursue the online course will not be entertained.
              </li>
              <li>
                No further incentive shall be permissible on successful completion of
                the online courses.
              </li>
              <li>
                Only the courses contained in the approved list shall be pursued for
                reimbursement and addition of qualification in service records.
              </li>
              <li>
                Employees are advised to avail discount if offered by the University/College.
              </li>
              <li>
                An employee can pursue any number of courses, however reimbursement
                shall be restricted to Rs. 18000 per block of 3 years.
              </li>
            </ul>
          </div>
        </div>

        {/* Attachments */}
        {Array.isArray(nocData?.documents) && nocData.documents.length > 0 && (
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-paperclip me-2 text-primary"></i>
                Application Attachments
              </h5>
            </div>
            <div className="card-body">
              <div className="d-flex flex-wrap gap-2">
                {nocData.documents.map((doc: any, idx: number) => (
                  <button
                    key={doc.id || idx}
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center"
                    onClick={() => handleDocumentClick(doc, "NOC_ONLINECOURSE", onShowToast)}
                  >
                    <i className="ti ti-file me-1"></i>
                    {doc.fileName?.split("/").pop() || `Attachment ${idx + 1}`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Work History */}
        <NocWorkHistoryCard nocData={nocData} />

        {/* Communication & Chat Section */}
        <NocChatSection
          nocData={nocData}
          nocType="NOC_ONLINECOURSE"
          onShowToast={onShowToast}
        />

        {/* Actions Modal */}
        <NocActionModal
          hRemarks={hRemarks}
          onHRemarksChange={setHRemarks}
          onApproveReject={handleApproveReject}
          isProcessing={isProcessing}
          processingAction={processingAction}
          showRemarksSection={true}
          remarksRequiredForReject={true}
        />
      </div>
    </div>
  );
};
